import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "lib/utils"

const alertVariants = cva(
  "relative w-full rounded-lg border px-4 py-3 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground [&>svg~*]:pl-7",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive:
          "border-destructive/80 bg-destructive text-destructive-foreground [&>svg]:text-destructive-foreground",
        warning:
          "border-amber-600/40 bg-amber-500 text-amber-950 dark:border-amber-500/50 dark:bg-amber-600 dark:text-amber-50 [&>svg]:text-amber-950 dark:[&>svg]:text-amber-50",
        info:
          "border-sky-600/40 bg-sky-600 text-white dark:border-sky-500/50 dark:bg-sky-700 dark:text-sky-50 [&>svg]:text-white dark:[&>svg]:text-sky-50",
        success:
          "border-emerald-600/40 bg-emerald-600 text-white dark:border-emerald-500/50 dark:bg-emerald-700 dark:text-emerald-50 [&>svg]:text-white dark:[&>svg]:text-emerald-50",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
))
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  // Title text always comes from children; rule cannot see spread props.
  // eslint-disable-next-line jsx-a11y/heading-has-content -- children via ...props
  <h5
    ref={ref}
    className={cn("mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  />
))
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

export { Alert, AlertTitle, AlertDescription }
